/**
 * Privacy Policy — BetUDontBet Poker / BUDB PKR
 */
import { LegalLayout } from "@/components/legal-layout";
import { BRAND } from "@shared/brand";

const UPDATED = "August 9, 2026";

export default function PrivacyPage() {
  return (
    <LegalLayout title="Privacy Policy" updated={UPDATED}>
      <p>
        This Privacy Policy explains how <strong>{BRAND.name}</strong> (
        <strong>{BRAND.shortName}</strong>) operated by Grudge Studio
        (&quot;we&quot;, &quot;us&quot;) collects, uses, and shares information
        when you use {BRAND.origin} and related services (the &quot;Service&quot;).
        Related legal terms: <a href="/tos">Terms of Service (/tos)</a>.
      </p>

      <h2>1. Who we are</h2>
      <p>
        The Service is a web application for multiplayer Texas Hold&apos;em and
        related games using the GBUX digital token on Solana, with optional
        Grudge Studio fleet identity (Grudge ID) and wallets. Contact for
        privacy questions:{" "}
        <a href="mailto:privacy@grudge-studio.com">privacy@grudge-studio.com</a>{" "}
        (or the contact channel listed on grudge-studio.com).
      </p>

      <h2>2. Information we collect</h2>
      <ul>
        <li>
          <strong>Wallet identifiers.</strong> Public Solana wallet addresses
          you connect via Phantom Connect / extension, Solflare, or other
          injectors, or Grudge Wallet. We do not receive your private keys or
          seed phrases. Phantom may process connection events under its own
          privacy policy when you use their software.
        </li>
        <li>
          <strong>Play ledger data.</strong> GBUX play balances, deposits,
          bets, table buy-ins/cash-outs, slot spins, hand history summaries,
          and related ledger events stored on our edge database (Cloudflare D1).
        </li>
        <li>
          <strong>Gameplay telemetry.</strong> Table IDs, seat choices,
          session timing, connection status, and device/browser technical data
          needed to run multiplayer tables (WebSocket sessions).
        </li>
        <li>
          <strong>Account profile fields.</strong> Optional display name /
          username, stats (hands played/won), and settings you save.
        </li>
        <li>
          <strong>On-chain verification data.</strong> Transaction signatures
          and amounts you submit so we can verify real GBUX deposits or SOL /
          USDT purchases to our sales agent wallet (BUDBai). Purchase proceeds
          settle on the agent; play GBUX is ledger-credited after verification.
        </li>
        <li>
          <strong>Authentication.</strong> If you use Grudge ID SSO, we receive
          identity tokens and linked account identifiers according to that
          service&apos;s policies.
        </li>
        <li>
          <strong>Logs &amp; security.</strong> Request IDs, IP-derived
          region/edge location (as provided by our CDN), error logs, and abuse
          prevention signals.
        </li>
      </ul>
      <p>
        We do <strong>not</strong> intentionally collect government ID, bank
        account numbers, or full payment card data through this poker site.
        Fiat rails (if any) are manual/optional and outside automated checkout.
      </p>

      <h2>3. How we use information</h2>
      <ul>
        <li>Operate tables, slots, wallets balances, and anti-cheat integrity.</li>
        <li>Credit play GBUX only after verified on-chain transfers.</li>
        <li>Provide account history, loyalty/stats, and admin house operations.</li>
        <li>Secure the Service, prevent fraud, free-chip abuse, and abuse.</li>
        <li>Improve performance, reliability, and product design.</li>
        <li>Comply with law and enforce our Terms of Service.</li>
      </ul>

      <h2>4. Sharing</h2>
      <p>We may share data with:</p>
      <ul>
        <li>
          <strong>Infrastructure processors</strong> — Cloudflare (Workers, D1,
          assets CDN), hosting and observability tools.
        </li>
        <li>
          <strong>Fleet services</strong> — Grudge ID, wallet APIs, and related
          Grudge Studio backends required for login and balances.
        </li>
        <li>
          <strong>Wallet providers</strong> — e.g. Phantom (browser extension /
          Phantom Connect SDK) process connection and signing on your device;
          they may receive domain and session metadata per their terms.
        </li>
        <li>
          <strong>Blockchain networks</strong> — Solana is public; transactions
          you broadcast are visible on-chain.
        </li>
        <li>
          <strong>Legal / safety</strong> — when required by law, or to protect
          rights, users, or the Service.
        </li>
      </ul>
      <p>
        We do <strong>not</strong> sell your personal information. We do not
        use play ledger data for third-party advertising profiles.
      </p>

      <h2>5. Cookies &amp; local storage</h2>
      <p>
        We use essential cookies/local storage for session continuity, wallet
        connection preference, and PWA install behavior. We do not run
        third-party ad trackers on the core play surfaces as of the date above.
      </p>

      <h2>6. Retention</h2>
      <p>
        Ledger, hand history, and account records are retained while your
        account is active and for a reasonable period afterward for fraud
        prevention, dispute resolution, and legal compliance. Server logs are
        retained for shorter operational windows unless needed for security
        investigations.
      </p>

      <h2>7. Security</h2>
      <p>
        We use HTTPS, edge isolation, and access-controlled admin tools.
        Blockchain keys stay in your wallet. No method of transmission or
        storage is 100% secure; use strong wallet hygiene and never share seed
        phrases.
      </p>

      <h2>8. Children</h2>
      <p>
        The Service is for users <strong>18 years or older</strong> (or the age
        of majority where higher). We do not knowingly collect data from minors.
      </p>

      <h2>9. International users</h2>
      <p>
        Data may be processed in the United States and other regions where our
        providers operate. By using the Service you understand transfers may
        occur as needed to run a global edge network.
      </p>

      <h2>10. Your choices</h2>
      <ul>
        <li>Disconnect your wallet and stop using the Service at any time.</li>
        <li>
          Request access or deletion of account records we control by emailing{" "}
          <a href="mailto:privacy@grudge-studio.com">privacy@grudge-studio.com</a>
          . We may retain minimal records required for legal/fraud reasons.
        </li>
        <li>
          On-chain history cannot be erased from public blockchains.
        </li>
      </ul>

      <h2>11. Changes</h2>
      <p>
        We may update this Policy. Material changes will be reflected by the
        &quot;Last updated&quot; date on this page. Continued use after changes
        means you accept the revised Policy.
      </p>

      <h2>12. Contact</h2>
      <p>
        Privacy requests:{" "}
        <a href="mailto:privacy@grudge-studio.com">privacy@grudge-studio.com</a>
        <br />
        Service: <a href={BRAND.origin}>{BRAND.origin}</a>
      </p>
    </LegalLayout>
  );
}
